var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var SiamRules_1;
import { MGPFallible, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { Orthogonal } from '../../jscaip/Orthogonal';
import { PlayerOrNone } from '../../jscaip/Player';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { Debug } from '../../utils/Debug';
import { MGPValidators } from '../../utils/MGPValidator';
import { SiamFailure } from './SiamFailure';
import { SiamMove } from './SiamMove';
import { SiamPiece } from './SiamPiece';
import { SiamState } from './SiamState';
export class SiamLegalityInformation {
    resultingBoard;
    moved;
    constructor(resultingBoard, moved) {
        this.resultingBoard = resultingBoard;
        this.moved = moved;
    }
}
export class SiamNode extends GameNode {
}
let SiamRules = class SiamRules extends ConfigurableRules {
    static { SiamRules_1 = this; }
    static singleton = MGPOptional.empty();
    static get() {
        if (SiamRules_1.singleton.isAbsent()) {
            SiamRules_1.singleton = MGPOptional.of(new SiamRules_1());
        }
        return SiamRules_1.singleton.get();
    }
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Siam`,
        config: {
            // minimum 3 so that there are spaces around the mountain
            width: new NumberConfig(5, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(3, 99)),
            height: new NumberConfig(5, RulesConfigDescriptionLocalizable.HEIGHT, MGPValidators.range(3, 99)),
            numberOfPiece: new NumberConfig(5, () => $localize `Number of piece by player`, MGPValidators.range(1, 99)),
            // -1 on two ends because there will always be the first mountain
            numberOfBonusMountain: new NumberConfig(2, () => $localize `Number of bonus mountains`, MGPValidators.range(0, 98)),
        },
    });
    getRulesConfigDescription() {
        return SiamRules_1.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState(config) {
        const board = TableUtils.create(config.width, config.height, SiamPiece.EMPTY);
        const cy = Math.floor(config.height / 2);
        const cx = Math.floor(config.width / 2);
        board[cy][cx] = SiamPiece.MOUNTAIN;
        config.numberOfBonusMountain = Math.min(config.numberOfBonusMountain, config.width - 1);
        let numberOfBonusMountainDropped = 0;
        while (numberOfBonusMountainDropped < config.numberOfBonusMountain) {
            const mountainExcentricity = Math.ceil((numberOfBonusMountainDropped + 1) / 2);
            board[cy][cx + mountainExcentricity] = SiamPiece.MOUNTAIN;
            numberOfBonusMountainDropped++;
            if (numberOfBonusMountainDropped < config.numberOfBonusMountain) {
                board[cy][cx - mountainExcentricity] = SiamPiece.MOUNTAIN;
                numberOfBonusMountainDropped++;
            }
        }
        return new SiamState(board, 0);
    }
    getMoveValidity(move, state) {
        const startedOutside = state.isNotOnBoard(move.coord);
        if (move.isRotation()) {
            if (startedOutside) {
                return MGPFallible.failure($localize `You cannot rotate piece outside the board: ${move.toString()}`);
            }
        }
        else {
            const finishedOutside = state.isNotOnBoard(move.coord.getNext(move.direction.get()));
            if (finishedOutside) {
                if (startedOutside) {
                    return MGPFallible.failure($localize `SiamMove should end or start on the board: ${move.toString()}`);
                }
                if (move.direction.get() !== move.landingOrientation) {
                    return MGPFallible.failure($localize `SiamMove should have moveDirection and landingOrientation matching when a piece goes out of the board: ${move.toString()}`);
                }
            }
        }
        return MGPValidation.SUCCESS;
    }
    isInsertion(move, state) {
        return move.coord.x === -1 ||
            move.coord.x === state.getWidth() ||
            move.coord.y === -1 ||
            move.coord.y === state.getHeight();
    }
    isLegal(move, state, config) {
        const moveValidity = this.getMoveValidity(move, state);
        if (moveValidity.isFailure()) {
            return moveValidity.toOtherFallible();
        }
        if (this.isInsertion(move, state) === false) {
            const movedPiece = state.getPieceAt(move.coord);
            if (movedPiece === SiamPiece.EMPTY) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
            }
            else if (movedPiece.belongsTo(state.getCurrentOpponent())) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
            }
        }
        if (move.isRotation()) {
            return this.isLegalRotation(move, state);
        }
        else {
            let movingPiece;
            if (this.isInsertion(move, state)) {
                const insertionInfo = this.isLegalInsertion(move.coord, state, config);
                if (insertionInfo.legal.isFailure()) {
                    return MGPFallible.failure(insertionInfo.legal.getReason());
                }
                movingPiece = insertionInfo.insertedPiece;
            }
            else {
                movingPiece = state.getPieceAt(move.coord);
            }
            return this.isLegalForwarding(move, state, movingPiece);
        }
    }
    isLegalInsertion(coord, state, config) {
        const numberOnBoard = state.countCurrentPlayerPawn();
        const currentPlayer = state.getCurrentPlayer();
        const legal = (numberOnBoard < config.numberOfPiece) ?
            MGPValidation.SUCCESS :
            MGPValidation.failure(SiamFailure.NO_REMAINING_PIECE_TO_INSERT());
        const insertedPiece = this.getInsertedPiece(coord, currentPlayer, state.getWidth());
        return { insertedPiece, legal };
    }
    getInsertedPiece(entrance, player, width) {
        if (entrance.x === -1)
            return SiamPiece.of(Orthogonal.RIGHT, player);
        if (entrance.y === -1)
            return SiamPiece.of(Orthogonal.DOWN, player);
        if (entrance.x === width)
            return SiamPiece.of(Orthogonal.LEFT, player);
        return SiamPiece.of(Orthogonal.UP, player);
    }
    isLegalForwarding(move, state, firstPiece) {
        Utils.assert(firstPiece !== SiamPiece.MOUNTAIN && firstPiece !== SiamPiece.EMPTY, 'forwarding must be done with player piece');
        const movedPieces = [];
        let movingPiece = SiamPiece.of(move.landingOrientation, state.getCurrentPlayer());
        const pushingDir = move.direction.get();
        let landingCoord = move.coord.getNext(pushingDir);
        if (state.hasInequalPieceAt(landingCoord, SiamPiece.EMPTY) &&
            this.isStraight(firstPiece, move) === false) {
            return MGPFallible.failure(SiamFailure.ILLEGAL_PUSH());
        }
        let currentDirection = MGPOptional.of(pushingDir);
        const resistingDir = pushingDir.getOpposite();
        let totalForce = 0;
        const resultingBoard = state.getCopiedBoard();
        if (state.isOnBoard(move.coord)) {
            resultingBoard[move.coord.y][move.coord.x] = SiamPiece.EMPTY;
            movedPieces.push(move.coord);
        }
        let pushingPossible = state.isOnBoard(landingCoord) &&
            movingPiece !== SiamPiece.EMPTY;
        while (pushingPossible) {
            if (currentDirection.equalsValue(pushingDir))
                totalForce++;
            else if (currentDirection.equalsValue(resistingDir))
                totalForce--;
            const tmpPiece = resultingBoard[landingCoord.y][landingCoord.x];
            if (tmpPiece === SiamPiece.MOUNTAIN)
                totalForce -= 0.9;
            resultingBoard[landingCoord.y][landingCoord.x] = movingPiece;
            movedPieces.push(landingCoord);
            movingPiece = tmpPiece;
            landingCoord = landingCoord.getNext(pushingDir);
            currentDirection = movingPiece.getOptionalDirection();
            pushingPossible = state.isOnBoard(landingCoord) &&
                movingPiece !== SiamPiece.EMPTY &&
                totalForce > 0;
        }
        if (state.isNotOnBoard(landingCoord)) {
            if (currentDirection.equalsValue(pushingDir))
                totalForce++;
            else if (currentDirection.equalsValue(resistingDir))
                totalForce--;
        }
        if (totalForce <= 0) {
            return MGPFallible.failure(SiamFailure.NOT_ENOUGH_FORCE_TO_PUSH());
        }
        return MGPFallible.success(new SiamLegalityInformation(resultingBoard, movedPieces));
    }
    isStraight(piece, move) {
        const pieceDirection = piece.getDirection();
        return (move.direction.equalsValue(pieceDirection) &&
            pieceDirection === move.landingOrientation);
    }
    isLegalRotation(rotation, state) {
        const coord = rotation.coord;
        const currentPiece = state.getPieceAt(coord);
        const currentPlayer = state.getCurrentPlayer();
        if (currentPiece.getDirection() === rotation.landingOrientation) {
            return MGPFallible.failure(SiamFailure.MUST_MOVE_OR_ROTATE());
        }
        const resultingBoard = state.getCopiedBoard();
        resultingBoard[coord.y][coord.x] = SiamPiece.of(rotation.landingOrientation, currentPlayer);
        return MGPFallible.success(new SiamLegalityInformation(resultingBoard, [coord]));
    }
    applyLegalMove(_move, state, _config, info) {
        const newBoard = TableUtils.copy(info.resultingBoard);
        const newTurn = state.turn + 1;
        const resultingState = new SiamState(newBoard, newTurn);
        return resultingState;
    }
    getScoreFromShortestDistances(zeroShortestDistance, oneShortestDistance, currentPlayer) {
        if (zeroShortestDistance === Number.POSITIVE_INFINITY)
            zeroShortestDistance = 6;
        if (oneShortestDistance === Number.POSITIVE_INFINITY)
            oneShortestDistance = 6;
        const zeroScore = 6 - zeroShortestDistance;
        const oneScore = 6 - oneShortestDistance;
        if (zeroScore === oneScore) {
            return currentPlayer.getScoreModifier();
        }
        else if (zeroScore > oneScore) {
            return (-10 * (zeroScore + 1)) + (oneScore + 1);
        }
        else {
            return (10 * (oneScore + 1)) - (zeroScore + 1);
        }
    }
    getMountainsRowsAndColumns(state) {
        const rows = [];
        const columns = [];
        let nbMountain = 0;
        for (const coordAndContent of state.getCoordsAndContents()) {
            if (coordAndContent.content === SiamPiece.MOUNTAIN) {
                if (rows.includes(coordAndContent.coord.y) === false) {
                    rows.push(coordAndContent.coord.y);
                }
                if (columns.includes(coordAndContent.coord.x) === false) {
                    columns.push(coordAndContent.coord.x);
                }
                nbMountain++;
            }
        }
        return { rows, columns, nbMountain };
    }
    getWinner(state, move, nbMountain, config) {
        if (nbMountain === config.numberOfBonusMountain) {
            return this.getPusher(state, move.get());
        }
        else {
            return PlayerOrNone.NONE;
        }
    }
    getPusher(state, finishingMove) {
        // here we will call the piece that started the move "moveStarter", obviously
        // and the piece in the right direction that was the closest to the falling mountain: the pusher
        const moveStarterCoord = finishingMove.coord;
        let moveStarterPiece;
        if (state.isOnBoard(moveStarterCoord)) {
            const moveStarterDir = finishingMove.landingOrientation;
            moveStarterPiece = state.getPieceAt(moveStarterCoord.getNext(moveStarterDir));
        }
        else { // insertion
            moveStarterPiece = this.getInsertedPiece(moveStarterCoord, state.getCurrentOpponent(), state.getWidth());
        }
        const pushingDirection = moveStarterPiece.getDirection();
        const pusherCoord = this.getPusherCoord(state, pushingDirection, moveStarterCoord);
        const winner = state.getPieceAt(pusherCoord).getOwner();
        Debug.display('SiamRules', 'getPusher', moveStarterCoord.toString() + ' belong to ' + state.getCurrentOpponent().getValue() + ', ' +
            pusherCoord.toString() + ' belong to ' + winner.getValue() + ', ' + winner.getValue() + ' win');
        return winner;
    }
    getPusherCoord(state, pushingDirection, pusher) {
        let pushed = pusher.getNext(pushingDirection);
        let lastCorrectPusher = pusher;
        while (state.isOnBoard(pushed)) {
            pusher = pushed;
            pushed = pushed.getNext(pushingDirection);
            const pushingPiece = state.getPieceAt(pusher);
            if (pushingPiece !== SiamPiece.MOUNTAIN && pushingPiece.getDirection() === pushingDirection) {
                lastCorrectPusher = pusher;
            }
        }
        return lastCorrectPusher;
    }
    getPushers(state, mountainsColumn, mountainsRow, config) {
        let pushers = [];
        const lineDirections = [];
        for (const x of mountainsColumn) {
            let direction = Orthogonal.DOWN;
            let fallingCoord = new Coord(x, config.height - 1);
            lineDirections.push({ direction, fallingCoord });
            direction = Orthogonal.UP;
            fallingCoord = new Coord(x, 0);
            lineDirections.push({ direction, fallingCoord });
        }
        for (const y of mountainsRow) {
            let direction = Orthogonal.LEFT;
            let fallingCoord = new Coord(0, y);
            lineDirections.push({ direction, fallingCoord });
            direction = Orthogonal.RIGHT;
            fallingCoord = new Coord(config.width - 1, y);
            lineDirections.push({ direction, fallingCoord });
        }
        for (const lineDirection of lineDirections) {
            const fallingCoord = lineDirection.fallingCoord;
            const direction = lineDirection.direction;
            pushers = this.addPotentialDirectionPusher(state, fallingCoord, direction, pushers, config);
        }
        return pushers;
    }
    addPotentialDirectionPusher(state, fallingCoord, direction, pushers, config) {
        const directionClosestPusher = this.getLineClosestPusher(state, fallingCoord, direction, config);
        if (directionClosestPusher.isAbsent()) {
            return pushers;
        }
        const pusher = directionClosestPusher.get();
        const distance = pusher.distance;
        const pusherCoord = pusher.coord;
        // find who own that pushing piece found
        pushers.push({
            coord: pusherCoord,
            distance, // : distance + malus
        });
        return pushers;
    }
    getInitialLineInfo(state, fallingCoord, direction) {
        return {
            resistance: direction.getOpposite(),
            previousPiece: state.getPieceAt(fallingCoord),
            closestPusher: {
                coord: fallingCoord,
                distance: 1,
            },
            almostPusher: MGPOptional.empty(),
            pusherFound: false,
            mountainEncountered: false,
            missingForce: 0,
        };
    }
    getLineClosestPusher(state, fallingCoord, direction, config) {
        let lineInfo = this.getInitialLineInfo(state, fallingCoord, direction);
        while (state.isOnBoard(lineInfo.closestPusher.coord) && lineInfo.pusherFound === false) {
            lineInfo = this.updateLineInfo(state, lineInfo, direction);
        }
        if (lineInfo.pusherFound === false && lineInfo.almostPusher.isPresent()) {
            lineInfo.closestPusher.distance++;
            lineInfo.missingForce -= 1;
            while (lineInfo.closestPusher.coord.equals(lineInfo.almostPusher.get()) === false) {
                lineInfo.closestPusher.coord = lineInfo.closestPusher.coord.getNext(direction);
                lineInfo.closestPusher.distance--;
            }
        }
        if (state.isNotOnBoard(lineInfo.closestPusher.coord)) {
            lineInfo.missingForce -= 1;
            if (state.countCurrentPlayerPawn() === config.numberOfPiece) {
                return MGPOptional.empty();
            }
        }
        if (lineInfo.missingForce > 0) {
            return MGPOptional.empty();
        }
        return MGPOptional.of(lineInfo.closestPusher);
    }
    updateLineInfo(state, lineInfo, direction) {
        const currentPiece = state.getPieceAt(lineInfo.closestPusher.coord);
        if (currentPiece.isEmptyOrMountain()) {
            if (currentPiece === SiamPiece.MOUNTAIN) {
                lineInfo.missingForce += 0.9;
                lineInfo.mountainEncountered = true;
            }
            else { // Encountered empty space
                lineInfo.closestPusher.distance++;
            }
        }
        else { // Player found
            const playerOrientation = currentPiece.getDirection();
            if (playerOrientation === direction) {
                if (lineInfo.mountainEncountered) {
                    lineInfo.missingForce -= 1; // We count her as active pusher
                    // We found a piece pushing right in the good direction
                    if (lineInfo.missingForce <= 0) { // And she has enough force to push
                        lineInfo.pusherFound = true;
                        lineInfo.closestPusher.coord =
                            lineInfo.closestPusher.coord.getNext(direction);
                    }
                }
            }
            else if (playerOrientation === lineInfo.resistance) {
                // We found a piece resisting the pushing direction
                lineInfo.missingForce += 1;
                if (lineInfo.mountainEncountered === false) {
                    lineInfo.closestPusher.distance++;
                }
            }
            else {
                if (lineInfo.mountainEncountered) {
                    lineInfo.almostPusher = MGPOptional.of(lineInfo.closestPusher.coord);
                    if (lineInfo.previousPiece !== SiamPiece.EMPTY) {
                        lineInfo.closestPusher.distance++;
                    }
                }
                else {
                    lineInfo.closestPusher.distance++;
                }
            }
        }
        // Still no player there, let's go back further
        lineInfo.previousPiece = currentPiece;
        lineInfo.closestPusher.coord = lineInfo.closestPusher.coord.getPrevious(direction);
        return lineInfo;
    }
    getInsertions(state, config) {
        let moves = [];
        const maxX = config.width - 1;
        const maxY = config.height - 1;
        for (let x = 1; x < maxX; x++) {
            moves = moves.concat(this.getInsertionsAt(state, x, 0, config));
            moves = moves.concat(this.getInsertionsAt(state, x, maxY, config));
        }
        for (let y = 1; y < maxY; y++) {
            moves = moves.concat(this.getInsertionsAt(state, 0, y, config));
            moves = moves.concat(this.getInsertionsAt(state, maxX, y, config));
        }
        moves = moves.concat(this.getInsertionsAt(state, 0, 0, config));
        moves = moves.concat(this.getInsertionsAt(state, 0, maxY, config));
        moves = moves.concat(this.getInsertionsAt(state, maxX, 0, config));
        moves = moves.concat(this.getInsertionsAt(state, maxX, maxY, config));
        return moves;
    }
    getInsertionsAt(state, x, y, config) {
        const moves = [];
        for (const direction of Orthogonal.ORTHOGONALS) {
            const entrance = new Coord(x, y).getPrevious(direction);
            if (state.isNotOnBoard(entrance)) {
                for (const orientation of Orthogonal.ORTHOGONALS) {
                    const move = SiamMove.of(entrance.x, entrance.y, MGPOptional.of(direction), orientation);
                    Utils.assert(this.getMoveValidity(move, state).isSuccess(), 'SiamRules.getInsertionsAt should only construct valid insertions');
                    const legality = this.isLegal(move, state, config);
                    if (legality.isSuccess()) {
                        moves.push(move);
                    }
                }
            }
        }
        return moves;
    }
    getMovesFrom(state, piece, x, y) {
        const coord = new Coord(x, y);
        // Three rotations
        let moves = this.getRotationMovesAt(coord, piece);
        for (const direction of Orthogonal.ORTHOGONALS) {
            // All legal forward moves
            const landingCoord = coord.getNext(direction);
            moves = moves.concat(this.getForwardMovesBetween(state, coord, landingCoord));
        }
        return moves;
    }
    getMovesBetween(state, piece, start, end) {
        if (start.equals(end)) {
            return this.getRotationMovesAt(start, piece);
        }
        else {
            const directionOpt = Orthogonal.factory.fromMove(start, end);
            if (directionOpt.isSuccess() && start.getLinearDistanceToward(end) === 1) {
                return this.getForwardMovesBetween(state, start, end);
            }
            else {
                // There are no possible moves here
                return [];
            }
        }
    }
    getRotationMovesAt(coord, piece) {
        const moves = [];
        const currentOrientation = piece.getDirection();
        for (const direction of Orthogonal.ORTHOGONALS) {
            if (direction !== currentOrientation) {
                const newMove = SiamMove.of(coord.x, coord.y, MGPOptional.empty(), direction);
                moves.push(newMove);
            }
        }
        return moves;
    }
    getForwardMovesBetween(state, start, end) {
        const moves = [];
        let orientations;
        const direction = Orthogonal.factory.fromMove(start, end).get();
        const piece = state.getPieceAt(start);
        if (state.isOnBoard(end)) {
            orientations = Orthogonal.ORTHOGONALS;
        }
        else {
            orientations = [direction];
        }
        for (const orientation of orientations) {
            const move = SiamMove.of(start.x, start.y, MGPOptional.of(direction), orientation);
            const legality = this.isLegalForwarding(move, state, piece);
            if (legality.isSuccess()) {
                moves.push(move);
            }
        }
        return moves;
    }
    getGameStatus(node, config) {
        const mountainsInfo = this.getMountainsRowsAndColumns(node.gameState);
        const winner = this.getWinner(node.gameState, node.previousMove, mountainsInfo.nbMountain, config);
        if (winner.isPlayer()) {
            return GameStatus.getVictory(winner);
        }
        else {
            return GameStatus.ONGOING;
        }
    }
};
SiamRules = SiamRules_1 = __decorate([
    Debug.log
], SiamRules);
export { SiamRules };
